import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import apiClient from '@/services/api';
import type { DeliveryChargeReport, DeliveryChargeRow } from '@/types/api';
import { ReportFrame, SimpleTable } from '@/components/reports/ReportFrame';
import type { ReportViewProps } from '@/components/reports/reportTypes';
import { buildGenericCsv, downloadCsv, printGenericReport, type ReportSection } from '@/utils/reportExport';

const HEADERS = ['Date', 'Ticket #', 'Order Type', 'Customer', 'Delivery Charge'];

/**
 * Delivery Charge: one row per ticket that carried a delivery charge (> 0),
 * with a grand-total row at the end. Voided/refunded tickets are excluded.
 */
export default function DeliveryChargeReport({ filters }: ReportViewProps) {
  const { selectedRestaurantId, startDate, endDate, formatCurrency, selectedRestaurant } = filters;

  const { data, isLoading, isError, refetch } = useQuery<DeliveryChargeReport>({
    queryKey: ['delivery-charge', selectedRestaurantId, startDate, endDate],
    queryFn: () => apiClient.getDeliveryChargeReport(selectedRestaurantId!, startDate, endDate),
    enabled: !!selectedRestaurantId,
  });

  const tickets = data?.tickets ?? [];
  const when = (iso: string | null) => (iso ? format(new Date(iso), 'yyyy-MM-dd HH:mm') : '—');
  const rowFor = (r: DeliveryChargeRow): (string | number)[] => [
    when(r.closedAt),
    r.ticketNumber ?? '—',
    r.orderType ?? '—',
    r.customerName ?? '—',
    formatCurrency(r.deliveryCharge),
  ];

  const bodyRows: (string | number)[][] = [
    ...tickets.map(rowFor),
    ...(data ? [['Total', '', '', String(data.ticketCount), formatCurrency(data.totalDeliveryCharge)]] : []),
  ];

  const meta = () => ({ restaurantName: selectedRestaurant?.name ?? '', startDate, endDate, formatCurrency });
  const sections = (): ReportSection[] => [{ heading: 'Delivery Charges', headers: HEADERS, rows: bodyRows }];

  return (
    <ReportFrame
      title="Delivery Charge"
      description="Every ticket with a delivery charge, plus the total for the period."
      hasRestaurant={!!selectedRestaurantId}
      isLoading={isLoading}
      isError={isError}
      onRetry={() => refetch()}
      isEmpty={tickets.length === 0}
      exportDisabled={!data}
      onExportCsv={() => data && downloadCsv(`delivery-charge-${format(new Date(), 'yyyy-MM-dd')}.csv`, buildGenericCsv('Delivery Charge', meta(), [], sections()))}
      onExportPdf={() => data && printGenericReport('Delivery Charge Report', meta(), [], sections())}
    >
      <div className="card"><div className="card-body">
        <SimpleTable headers={HEADERS} rows={bodyRows} />
      </div></div>
    </ReportFrame>
  );
}
